/**
 * Hosted Agent types for the Azure AI Foundry hosted agent integration.
 *
 * These types model the full lifecycle of a hosted agent: creation wizard,
 * connections.json storage, health checks, and diagnostics.
 *
 * Stored in connections.json under `hostedAgentConnections` — separate from
 * `agentConnections` (which are model provider configs for agent workflow actions).
 */

// =============================================================================
// CONNECTION MODEL (stored in connections.json)
// =============================================================================

/** Connection health status — drives the status badge in the UI */
export type HostedAgentStatus =
  | 'connected'
  | 'provisioning'
  | 'auth_failed'
  | 'agent_error'
  | 'version_mismatch'
  | 'unreachable'
  | 'unknown';

/** How far the creation wizard progressed — used by diagnose page to resume */
export type SetupProgress =
  | 'agent_created'
  | 'identities_discovered'
  | 'rbac_assigned'
  | 'complete';

/** A hosted agent connection entry in connections.json */
export interface HostedAgentConnection {
  connectionId: string;
  displayName: string;
  status: HostedAgentStatus;
  setupProgress: SetupProgress;
  configuration: {
    subscriptionId: string;
    resourceGroup: string;
    aiAccountName: string;
    projectName: string;
    agentName: string;
    modelDeployment: string;
    imageTag: string;
    acrImage: string;
    createdAt: string;
  };
  identity: {
    blueprintPrincipalId: string | null;
    instancePrincipalId: string | null;
    logicAppManagedIdentityConfigured: boolean;
  };
  endpoints: {
    projectEndpoint: string;
    invocationsUrl: string;
    openaiEndpoint: string;
  };
}

// =============================================================================
// WIZARD STATE
// =============================================================================

export type WizardStep = 'subscription' | 'project' | 'model' | 'name' | 'creating';

export interface HostedAgentWizardState {
  step: WizardStep;
  subscriptionId?: string;
  aiAccountName?: string;
  resourceGroup?: string;
  projectName?: string;
  projectEndpoint?: string;
  openaiEndpoint?: string;
  modelDeployment?: string;
  agentName?: string;
}

// =============================================================================
// API RESPONSE TYPES
// =============================================================================

/** Agent version from Foundry API */
export interface AgentVersionResponse {
  name: string;
  version: string;
  status: 'creating' | 'provisioning' | 'active' | 'failed';
  error?: {
    code: string;
    message: string;
  };
  blueprint?: {
    principal_id: string;
    client_id: string;
  };
  instance_identity?: {
    principal_id: string;
    client_id: string;
  };
}

/** Health check response from the agent container */
export interface HealthCheckResponse {
  status: 'healthy' | 'unhealthy' | 'version_mismatch';
  image_version: string;
  image_version_match?: boolean;
  copilot_session: string;
  model_auth: string;
  model_auth_error?: string;
}

/** Debug auth response for discovering instance identity */
export interface DebugAuthResponse {
  blueprint_cognitiveservices?: {
    aud: string;
    oid: string;
    status: number;
  };
  instance_cognitiveservices?: {
    aud: string;
    oid: string;
    status: number;
  };
  none_cognitiveservices?: {
    aud: string;
    oid: string;
    status: number;
  };
}

/** Foundry project from the projects API */
export interface FoundryProject {
  name: string;
  displayName?: string;
}

/** Model deployment from the OpenAI deployments API */
export interface OpenAIDeployment {
  id: string;
  model: string;
  status: string;
  sku?: {
    name: string;
    capacity: number;
  };
}

/** AI Account (CognitiveServices) from ARM */
export interface AiAccount {
  id: string;
  name: string;
  location: string;
  kind: string;
  resourceGroup: string;
  properties?: {
    endpoints?: {
      'AI Foundry Portal'?: string;
      'OpenAI Language Model Instance API'?: string;
    };
  };
}

// =============================================================================
// DIAGNOSTIC TYPES
// =============================================================================

export type DiagnosticStatus = 'pass' | 'fail' | 'pending' | 'loading';

export interface DiagnosticCheck {
  id: string;
  label: string;
  status: DiagnosticStatus;
  detail?: string;
  fixAvailable?: boolean;
}

// =============================================================================
// RBAC CONSTANTS
// =============================================================================

/** Cognitive Services OpenAI User — assigned to agent identities */
export const ROLE_COGNITIVE_SERVICES_OPENAI_USER = '5e0bd9bd-7b93-4f28-af87-19fc36ad61bd';

/** Azure AI User — covers Microsoft.CognitiveServices/* data actions */
export const ROLE_AZURE_AI_USER = '53ca6127-db72-4b80-b1b0-d745d6d5456d';

/** Azure AI Developer — grants Microsoft.MachineLearningServices/workspaces/* (covers agent
 *  invocation on an ML-workspace Foundry). Does NOT grant Microsoft.CognitiveServices/accounts/
 *  AIServices/agents/*, so a CognitiveServices-based Foundry account also needs ROLE_AZURE_AI_USER
 *  (whose Microsoft.CognitiveServices/* dataAction covers AIServices/agents/write). The Logic App
 *  MI is granted BOTH so invocation works regardless of the Foundry account's resource provider. */
export const ROLE_AZURE_AI_DEVELOPER = '64702f94-c441-49e6-a78b-ef80e0188fee';

/** Required header for Foundry hosted agent invocations */
export const FOUNDRY_FEATURES_HEADER = 'HostedAgents=V1Preview';

/** Default container image for hosted agents */
export const DEFAULT_HOSTED_AGENT_IMAGE = 'mcr.microsoft.com/azurelogicapps/logicapps-hosted-agent-shim:preview';

